import { Fragment, useState, useContext, useEffect } from 'react';
import { addKapton, deleteKapton, getKaptonTypeDisplay, kaptonGeometryOptions, kaptonSourceDisplay, kaptonSourceOptions, updateKapton } from '../services/kaptonService';
import ProtectedComponent from './common/ProtectedComponent';
import { Button } from 'react-bootstrap'

import { AuthContext } from './Login/AuthContext';
import { DatabaseContext } from './DatabaseContext';

import Icons from './common/Icons';
import { dateYYYYMMMDD, validateAlphanumeric, validateNonNegativeInt, validatePositiveInt } from '../services/commonParse';
import { userOptions } from '../services/userService.js'
import { findTableRow } from '../services/tableUtils';
import TableViewAdd, { defaultAdd, defaultResetAdd, defaultValidateAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultReset, defaultEdit, defaultValidateEdit } from './common/TableViewEdit';
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect } from './common/SimpleForm';
import PaginateTable, { CategoryFilter, FuzzyTextFilter, PassFailFilter } from './common/TableDisplay';
import { SimpleFold, GeometryCard } from './common/misc';

// Import images for selection
import F0W from "../images/kapton/F0W.png"
import TLW from "../images/kapton/TLW.png"
import BLW from "../images/kapton/BLW.png"
import LLW from "../images/kapton/LLW.png"
import RLW from "../images/kapton/RLW.png"
import FiveLW from "../images/kapton/5LW.png"
import LHW from "../images/kapton/LHW.png"
import RHW from "../images/kapton/RHW.png"
import BHW from "../images/kapton/BHW.png"

import F0T from "../images/kapton/F0T.png"
import TLT from "../images/kapton/TLT.png"
import BLT from "../images/kapton/BLT.png"
import LLT from "../images/kapton/LLT.png"
import RLT from "../images/kapton/RLT.png"
import FiveLT from "../images/kapton/5LT.png"
import THT from "../images/kapton/THT.png"


const KaptonColumns = () => [
  { Header: 'Kapton Batch ID', Cell: KaptonCellID, Filter: FuzzyTextFilter((kap) => [kap.batch_id]) },
  { Header: 'Manufacturer', Cell: (kap) => kaptonSourceDisplay(kap.source), Filter: CategoryFilter((kap) => kap.source) },
  { Header: "Geometry", Cell: KaptonCellType, Filter: CategoryFilter((kap) => kap.type) },
  { Header: 'Available', Cell: KaptonCellAvailable, Filter: PassFailFilter((kap) => kap.available > 0, "Hide empty", true) },
  { Header: 'Used', Cell: KaptonCellUsed },
  { Header: 'Delivery total', Cell: (kap) => kap.delivery_count },
  { Header: 'Defective count', Cell: (kap) => kap.defective_count },
  { Header: 'Date (CEST)', Cell: (kap) => <div>Rec/Exp | {dateYYYYMMMDD(kap.batch_reception_date)} / {dateYYYYMMMDD(kap.expiry_date)}</div> },
  { Header: 'Comment', Cell: (kap) => kap.comment },
]

const KaptonCellID = (kapton) => {
  const id = kapton.id;
  const { user } = useContext(AuthContext);

  // For showing single items
  const [showEdit, setShowEdit] = useState(false);
  const { updateKaptonList } = useContext(DatabaseContext);
  const removeAction = async () => {
    await deleteKapton(id);
    updateKaptonList();
  }

  return <Fragment>
    {kapton.batch_id}
    <Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}>{Icons.edit}</Button>
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    {showEdit && <KaptonEdit id={id} showState={[showEdit, setShowEdit]} />}
  </Fragment>
}

const KaptonCellType = (kap) => {
  const type = kap.type;
  return <Fragment>
    {getKaptonTypeDisplay(type)} v{kap.version} ({type})
  </Fragment>
}

const KaptonCellAvailable = (kap) => {
  const available = kap.available;
  return <Fragment>{available <= 0 ? <b>{available}</b> : available}</Fragment>
}
const KaptonCellUsed = (kap) => {
  const available = kap.used_count;
  return <Fragment>{available <= 0 ? <b>{available}</b> : available}</Fragment>
}

const KaptonEdit = ({ id, showState }) => {
  const [, setShow] = showState;
  const { kaptonList, updateKaptonList, userList } = useContext(DatabaseContext);
  const origData = findTableRow(kaptonList, (x) => x.id === id)
  const [invalid, setInvalid] = useState("");
  const form = {
    'batch_id': useState(origData.batch_id),
    'batch_reception_date': useState(origData.batch_reception_date),
    'source': useState(origData.source),
    "receiver": useState(origData.receiver),
    "expiry_date": useState(origData.expiry_date),
    "type": useState(origData.type),
    "version": useState(origData.version),
    "delivery_count": useState(origData.delivery_count),
    "defective_count": useState(origData.defective_count),
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    "receiver": useState(""),
    "source": useState(""),
    "type": useState(""),
    "version": useState(""),
    "delivery_count": useState(""),
    "defective_count": useState("")
  };

  const resetAction = defaultReset(form, origData);
  const editAction = defaultEdit(id, form, updateKapton, updateKaptonList, setShow);
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewEdit title={`Edit Tape order [${id}][${origData.batch_id}]`} showState={showState} resetAction={resetAction} editAction={editAction} invalid={invalid}>
    <HGCFormRowDate label="Batch Reception Date" dataState={form["batch_reception_date"]} />
    <HGCFormRowSelect label="Manufacturer" dataState={form["source"]} errorState={errorForm["source"]} options={kaptonSourceOptions} allowNull={false} />
    <HGCFormRowSelect label="Reciever" dataState={form["receiver"]} errorState={errorForm["receiver"]} options={userOptions(userList)} />
    <HGCFormRowDate label="Expiry Date" dataState={form["expiry_date"]} />
    <HGCFormRowSelect label="Geometry" dataState={form["type"]} errorState={errorForm["type"]} options={kaptonGeometryOptions} allowNull={false} />
    <HGCFormRowText label="Version" dataState={form["version"]} errorState={errorForm["version"]} validate={validatePositiveInt()} />
    <HGCFormRowText label="Delivery count" dataState={form["delivery_count"]} errorState={errorForm["delivery_count"]} validate={validatePositiveInt()} />
    <HGCFormRowText label="Defective count" dataState={form["defective_count"]} errorState={errorForm["defective_count"]} validate={validateNonNegativeInt()} />
  </TableViewEdit>
}


const GeometryReference = () => {
  return <>
    <SimpleFold header='CuW Geometry Reference (no corner "mousebites", bonding pads up)'>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <GeometryCard src={F0W} bodytext={"F0W (CuW/CEE Full)"} />
        <GeometryCard src={LLW} bodytext={"SLW (CuW/CEE LD Left)"} />
        <GeometryCard src={RLW} bodytext={"SLW (CuW/CEE LD Right)"} />
        <GeometryCard src={FiveLW} bodytext={"5LW (CuW/CEE LD Five)"} />
        <GeometryCard src={LHW} bodytext={"SHW (CuW/CEE HD Left)"} />
        <GeometryCard src={RHW} bodytext={"SHW (CuW/CEE HD Right)"} />
        <GeometryCard src={BHW} bodytext={"BHW (CuW/CEE HD Bottom)"} />
        <GeometryCard src={BLW} bodytext={"BLW (CuW/CEE LD Bottom)"} />
        <GeometryCard src={TLW} bodytext={"TLW (CuW/CEE LD Top)"} />
      </div >
    </SimpleFold>
    <SimpleFold header='Ti Geometry Reference ("mousebite" corners, bonding pads up)' >
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <GeometryCard src={F0T} bodytext={"F0T (Ti/CEH Full)"} />
        <GeometryCard src={LLT} bodytext={"LLT (CuW/CEH LD Left)"} />
        <GeometryCard src={RLT} bodytext={"RLT (CuW/CEH LD Right)"} />
        <GeometryCard src={THT} bodytext={"THT (CuW/CEH HD Top)"} />
        <GeometryCard src={TLT} bodytext={"TLT (CuW/CEH LD Top)"} />
        <GeometryCard src={BLT} bodytext={"BLT (CuW/CEH LD Bottom)"} />
        <GeometryCard src={FiveLT} bodytext={"5LT (CuW/CEH LD Five)"} />
      </div >
    </SimpleFold >
  </>
}


const KaptonAdd = ({ showState }) => {
  const { updateKaptonList, userList } = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const [, setShow] = showState;
  const [invalid, setInvalid] = useState("");
  const form = {
    'batch_id': useState(null),
    'batch_reception_date': useState(new Date()),
    'source': useState(null),
    "receiver": useState(user.username),
    "expiry_date": useState(new Date()),
    "type": useState(null),
    "version": useState(null),
    "delivery_count": useState(null),
    "defective_count": useState(0),
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    'source': useState(""),
    "receiver": useState(""),
    "type": useState(""),
    "version": useState(""),
    "delivery_count": useState(""),
    "defective_count": useState("")
  };

  const resetAction = defaultResetAdd(form, { 'batch_reception_date': () => new Date(), 'receiver': user.username })
  const addAction = defaultAdd(form, addKapton, updateKaptonList, resetAction, setShow);
  useEffect(() => { setInvalid(defaultValidateAdd(errorForm)) }, [errorForm])

  return <TableViewAdd title="Add new Kapton order" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="Batch id" dataState={form["batch_id"]} errorState={errorForm["batch_id"]} validate={validateAlphanumeric(true)} autoFocus={true} />
    <HGCFormRowDate label="Batch Reception Date" dataState={form["batch_reception_date"]} />
    <HGCFormRowSelect label="Manufacturer" dataState={form["source"]} errorState={errorForm["source"]} options={kaptonSourceOptions} allowNull={false} />
    <HGCFormRowSelect label="Reciever" dataState={form["receiver"]} options={userOptions(userList)} />
    <HGCFormRowDate label="Expiry Date" dataState={form["expiry_date"]} />
    <HGCFormRowSelect label="Geometry type" dataState={form["type"]} errorState={errorForm["type"]} options={kaptonGeometryOptions} allowNull={false} />
    <HGCFormRowText label="Version" dataState={form["version"]} errorState={errorForm["version"]} validate={validatePositiveInt()} />
    <HGCFormRowText label="Delivery count" dataState={form["delivery_count"]} errorState={errorForm["delivery_count"]} validate={validatePositiveInt(true)} />
    <HGCFormRowText label="Defective count" dataState={form["defective_count"]} errorState={errorForm["defective_count"]} validate={validateNonNegativeInt()} />
    <GeometryReference />
  </TableViewAdd>
}

export const KaptonList = () => {
  const { kaptonList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>Add Kapton</Button>
    <KaptonAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={kaptonList} columnLayout={KaptonColumns()} entriesPerPage={10} />
  </ProtectedComponent>
}

export default KaptonList;


